import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

// Simple SVG Icons
const XIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const ChevronLeftIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRightIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const GalleryModal = ({ isOpen, onClose, images, title }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Preload all images when modal opens
  useEffect(() => {
    if (isOpen && images) {
      images.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [isOpen, images]);

  // Preload adjacent images
  useEffect(() => {
    if (images && images.length > 0) {
      const nextImg = new Image();
      nextImg.src = images[(currentIndex + 1) % images.length];
      
      const prevImg = new Image();
      prevImg.src = images[(currentIndex - 1 + images.length) % images.length];
    }
  }, [currentIndex, images]);

  const handleNext = useCallback(() => {
    if (!images || images.length === 0 || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setIsTransitioning(false);
    }, 50);
  }, [images, isTransitioning]);

  const handlePrevious = useCallback(() => {
    if (!images || images.length === 0 || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsTransitioning(false);
    }, 50);
  }, [images, isTransitioning]);

  const goToImage = (index) => {
    if (index === currentIndex || isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 50);
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handlePrevious, handleNext, onClose]);

  if (!images || images.length === 0) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`relative w-full max-w-7xl max-h-[90vh] ${isDark ? 'bg-afsc-charcoal' : 'bg-white'} rounded-2xl shadow-2xl overflow-hidden flex flex-col`}>
              {/* Header */}
              <div className={`flex items-center justify-between p-4 md:p-6 border-b flex-shrink-0 ${isDark ? 'border-afsc-soft-gray/20' : 'border-gray-200'}`}>
                <h2 className={`text-xl md:text-2xl font-heading font-bold ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                  {title}
                </h2>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg transition-all duration-200 ${isDark ? 'hover:bg-afsc-gold/20 text-white hover:text-afsc-gold border border-transparent hover:border-afsc-gold/30' : 'hover:bg-afsc-gold/10 text-afsc-charcoal hover:text-afsc-gold border border-transparent hover:border-afsc-gold/30'}`}
                  aria-label="Close"
                >
                  <XIcon size={24} />
                </button>
              </div>

              {/* Main Image Container */}
              <div 
                className="relative flex items-center justify-center p-4 md:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900"
                style={{ height: 'calc(90vh - 200px)', minHeight: '400px' }}
              >
                {/* Previous Button */}
                {images.length > 1 && (
                  <button
                    onClick={handlePrevious}
                    className={`absolute left-2 md:left-4 z-20 p-3 md:p-4 rounded-full shadow-xl ${isDark ? 'bg-afsc-gold hover:bg-afsc-gold-deep text-afsc-charcoal border-2 border-afsc-gold' : 'bg-afsc-gold hover:bg-afsc-gold-deep text-afsc-charcoal border-2 border-afsc-gold'} transition-all duration-200 hover:scale-110 active:scale-95`}
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon size={28} className="md:w-8 md:h-8" />
                  </button>
                )}

                {/* Current Image */}
                <div
                  className="w-full h-full flex items-center justify-center px-12 md:px-16"
                  style={{ position: 'absolute', inset: 0 }}
                >
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.img
                      key={currentIndex}
                      src={images[currentIndex]}
                      alt={`${title} - Image ${currentIndex + 1}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-lg"
                      style={{ 
                        maxHeight: '100%',
                        maxWidth: '100%',
                        position: 'absolute'
                      }}
                    />
                  </AnimatePresence>
                </div>

                {/* Next Button */}
                {images.length > 1 && (
                  <button
                    onClick={handleNext}
                    className={`absolute right-2 md:right-4 z-20 p-3 md:p-4 rounded-full shadow-xl ${isDark ? 'bg-afsc-gold hover:bg-afsc-gold-deep text-afsc-charcoal border-2 border-afsc-gold' : 'bg-afsc-gold hover:bg-afsc-gold-deep text-afsc-charcoal border-2 border-afsc-gold'} transition-all duration-200 hover:scale-110 active:scale-95`}
                    aria-label="Next image"
                  >
                    <ChevronRightIcon size={28} className="md:w-8 md:h-8" />
                  </button>
                )}

                {/* Image Counter */}
                {images.length > 1 && (
                  <div className={`absolute top-4 right-4 px-4 py-2 rounded-full text-sm font-semibold ${isDark ? 'bg-afsc-gold text-afsc-charcoal border-2 border-afsc-gold' : 'bg-afsc-gold text-afsc-charcoal border-2 border-afsc-gold'} shadow-xl`}>
                    {currentIndex + 1} / {images.length}
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className={`p-4 md:p-6 border-t flex-shrink-0 ${isDark ? 'border-afsc-soft-gray/20 bg-afsc-charcoal' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex gap-2 md:gap-4 overflow-x-auto pb-2 scrollbar-hide">
                    {images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => goToImage(index)}
                        className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                          currentIndex === index
                            ? 'border-afsc-gold ring-2 ring-afsc-gold/50'
                            : isDark
                            ? 'border-afsc-soft-gray/30 hover:border-afsc-gold/50'
                            : 'border-gray-300 hover:border-afsc-gold/50'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GalleryModal;

